import React, { useState } from 'react';
import { X, MapPin, Users, Trophy, School, ChevronRight } from 'lucide-react';
import mapData from '../mapData.json';
import RegionDetailPanel from './RegionDetailPanel';

function pathwayRowCount(region) {
  const keys = ['modified_male', 'modified_female', 'junior_male', 'junior_female', 'senior_male', 'senior_female'];
  return keys.reduce((s, k) => s + (Number(region[k]) || 0), 0);
}

/** Map shading: session-based participants + competition row counts (visual only; datasets stay separate in the panel). */
function getActivityLevel(region, sessionTotal, competitionRows) {
  const inv = sessionTotal != null ? sessionTotal : region.involvement || 0;
  const comp = competitionRows != null ? competitionRows : pathwayRowCount(region);
  const combined = inv + comp;
  if (combined === 0) return 0;
  if (combined < 500) return 1;
  if (combined < 2000) return 2;
  if (combined < 5000) return 3;
  return 4;
}

const FILL_COLORS = [
  '#E2E8F0',   // 0: no data
  '#BFDBFE',   // 1: minimal
  '#60A5FA',   // 2: low
  '#2563EB',   // 3: medium
  '#0B3D91',   // 4: high
];

export default function MapSection({ regions, selectedRegion, regionDetail, onSelectRegion, sessionsByRegion }) {
  const [hoveredRegion, setHoveredRegion] = useState(null);

  const regionMap = {};
  regions.forEach(r => { regionMap[r.name] = r; });

  const sessionTotalByName = {};
  (sessionsByRegion || []).forEach((row) => {
    if (row.region && row.region !== 'Unknown') {
      sessionTotalByName[row.region] = row.total || 0;
    }
  });

  return (
    <div className="map-section" data-testid="map-section">
      <div className="glass-card map-panel">
        <h2>Namibia — Cricket Development Activity</h2>
        <p className="map-subtitle">Click a region to explore detailed metrics</p>
        <div className="map-wrapper" data-testid="map-wrapper">
          <svg
            viewBox={mapData.viewBox}
            xmlns="http://www.w3.org/2000/svg"
            style={{ width: '100%', height: 'auto' }}
          >
            {mapData.regions.map(r => {
              const data = regionMap[r.name] || {};
              const sessionTotal = sessionTotalByName[r.name];
              const level = getActivityLevel(data, sessionTotal, pathwayRowCount(data));
              const isSelected = selectedRegion === r.name;
              const isHovered = hoveredRegion === r.name;

              return (
                <g key={r.name}>
                  <path
                    d={r.path}
                    fill={isSelected ? '#E31837' : isHovered ? '#4FC3F7' : FILL_COLORS[level]}
                    stroke="white"
                    strokeWidth={isSelected || isHovered ? 2.5 : 1.2}
                    style={{
                      cursor: 'pointer',
                      transition: 'fill 0.25s, stroke-width 0.2s',
                      filter: isSelected ? 'drop-shadow(0 2px 6px rgba(227,24,55,0.3))' : 'none',
                    }}
                    onClick={() => onSelectRegion(r.name)}
                    onMouseEnter={() => setHoveredRegion(r.name)}
                    onMouseLeave={() => setHoveredRegion(null)}
                    data-testid={`map-region-${r.name.toLowerCase().replace(/\s/g, '-')}`}
                  />
                  <text
                    x={r.cx}
                    y={r.cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill={level >= 3 || isSelected ? '#fff' : '#334155'}
                    fontSize="11"
                    fontFamily="Inter, sans-serif"
                    fontWeight="600"
                    pointerEvents="none"
                    style={{ textShadow: level >= 3 || isSelected ? '0 1px 2px rgba(0,0,0,0.3)' : 'none' }}
                  >
                    {r.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Tooltip */}
        {hoveredRegion && !selectedRegion && regionMap[hoveredRegion] && (
          <div style={{
            position: 'absolute', bottom: '5rem', left: '1.5rem',
            background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
            borderRadius: 12, padding: '0.75rem 1rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            border: '1px solid rgba(0,0,0,0.06)',
            fontSize: '0.82rem', minWidth: 180, zIndex: 20,
          }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem', color: 'var(--brand-primary)' }}>
              {hoveredRegion}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginBottom: '0.35rem' }}>
              {regionMap[hoveredRegion]?.capital || ''}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem' }}>
              <span>
                <strong>{(sessionTotalByName[hoveredRegion] ?? regionMap[hoveredRegion]?.involvement ?? 0).toLocaleString()}</strong>{' '}
                involvement participants (sessions)
              </span>
              <span>
                <strong>{pathwayRowCount(regionMap[hoveredRegion] || {}).toLocaleString()}</strong> competition rows
              </span>
            </div>
          </div>
        )}

        <div className="map-legend">
          <span className="legend-label">Activity Level:</span>
          {['No data', 'Minimal', 'Low', 'Medium', 'High'].map((label, i) => (
            <div key={i} className="legend-item">
              <div className="legend-swatch" style={{ background: FILL_COLORS[i] }} />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Region Detail Panel */}
      <div className="glass-card region-panel" data-testid="region-panel">
        {selectedRegion && regionDetail ? (
          <RegionDetailPanel
            regionDetail={regionDetail}
            regionData={regionMap[selectedRegion]}
            onClose={() => onSelectRegion(null)}
          />
        ) : (
          <div className="region-panel-empty">
            <div className="icon-circle">
              <MapPin size={28} color="var(--brand-primary)" />
            </div>
            <p><strong>Click any region</strong> on the map to explore cricket development metrics for 2025.</p>
            <p className="hint">Involvement (Data Capture) and competitions/registers are shown separately — linked only by region name.</p>
          </div>
        )}
      </div>
    </div>
  );
}
